import React, { useState, useEffect } from 'react';
import {
  getAuditorAccess, inviteAuditor,
  revokeAuditor, deleteAuditor,
} from './db';

const ALL_FRAMEWORKS = [
  { id: 'cmmc_l1',  label: 'CMMC Level 1'     },
  { id: 'cmmc_l2',  label: 'CMMC Level 2'     },
  { id: 'cmmc_l3',  label: 'CMMC Level 3'     },
  { id: 'nist_171', label: 'NIST SP 800-171'  },
  { id: 'soc2',     label: 'SOC 2'            },
  { id: 'iso27001', label: 'ISO 27001'         },
  { id: 'hipaa',    label: 'HIPAA'            },
  { id: 'nist_csf', label: 'NIST CSF 2.0'     },
  { id: 'pci_dss',  label: 'PCI DSS v4.0'     },
  { id: 'fedramp',  label: 'FedRAMP Moderate' },
];
const FW_LABEL = ALL_FRAMEWORKS.reduce((acc, f) => { acc[f.id] = f.label; return acc; }, {});

// Simple inline SVG icons
function IconLink() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71"/>
      <path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71"/>
    </svg>
  );
}
function IconCopy() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/>
    </svg>
  );
}
function IconCheck() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12"/>
    </svg>
  );
}
function IconShield() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
    </svg>
  );
}
function IconPerson() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/>
    </svg>
  );
}

export default function AuditorAccess({ session }) {
  const [auditors, setAuditors]   = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm]           = useState({
    name: '', firm: '', email: '', framework: 'cmmc_l2', expiryDays: 90,
  });
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState('');
  const [copied, setCopied]       = useState(null);
  // After generation — show the link in modal
  const [generatedRecord, setGeneratedRecord] = useState(null);

  useEffect(() => {
    if (session) loadAuditors();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session]);

  async function loadAuditors() {
    const data = await getAuditorAccess(session.user.id);
    setAuditors(data);
  }

  function resetForm() {
    setForm({ name: '', firm: '', email: '', framework: 'cmmc_l2', expiryDays: 90 });
    setError('');
    setGeneratedRecord(null);
  }

  async function handleInvite() {
    if (!form.name.trim()) { setError('Auditor name is required.'); return; }
    if (!form.firm.trim()) { setError('Auditor firm or organization is required.'); return; }
    setLoading(true);
    setError('');
    const result = await inviteAuditor(
      session.user.id,
      form.name.trim(),
      form.firm.trim(),
      form.email.trim(),
      form.framework,
      Number(form.expiryDays),
    );
    if (result) {
      setGeneratedRecord(result);
      await loadAuditors();
    } else {
      setError('Failed to generate access link. Please try again.');
    }
    setLoading(false);
  }

  async function handleRevoke(id, name) {
    const label = name || 'this auditor';
    if (!window.confirm(`Are you sure you want to revoke access for ${label}? They will no longer be able to view the compliance dashboard.`)) return;
    await revokeAuditor(id);
    await loadAuditors();
  }

  async function handleDelete(id) {
    await deleteAuditor(id);
    await loadAuditors();
  }

  function getAuditorLink(token, framework) {
    return `${window.location.origin}?auditor=${token}${framework ? `&fw=${framework}` : ''}`;
  }

  function copyLink(token, framework, id) {
    navigator.clipboard.writeText(getAuditorLink(token, framework));
    setCopied(id);
    setTimeout(() => setCopied(null), 2000);
  }

  function getDaysRemaining(expiresAt) {
    const days = Math.ceil((new Date(expiresAt) - new Date()) / (1000 * 60 * 60 * 24));
    return Math.max(0, days);
  }

  function formatExpiry(expiresAt) {
    if (!expiresAt) return '—';
    return new Date(expiresAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  }

  const inputStyle = {
    width: '100%', background: 'var(--surface)', border: '1px solid var(--border2)',
    borderRadius: 6, padding: '9px 12px', fontSize: 13, color: 'var(--text)',
    fontFamily: 'var(--font)', outline: 'none', boxSizing: 'border-box',
  };
  const labelStyle = {
    fontSize: 11, color: 'var(--text3)', marginBottom: 5,
    display: 'block', textTransform: 'uppercase', letterSpacing: '0.06em',
  };

  return (
    <div style={{ padding: 28, overflowY: 'auto', height: '100%' }}>

      {/* Header */}
      <div style={{
        display: 'flex', justifyContent: 'space-between',
        alignItems: 'flex-start', marginBottom: 20,
      }}>
        <div>
          <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 4 }}>Auditor Access</div>
          <div style={{ fontSize: 12, color: 'var(--text3)' }}>
            Grant read-only access to your compliance data for C3PAO assessors and auditors
          </div>
        </div>
        <button
          onClick={() => { resetForm(); setShowModal(true); }}
          style={{
            fontSize: 12, fontWeight: 600, padding: '8px 16px', borderRadius: 6,
            cursor: 'pointer', border: '1px solid var(--accent)',
            background: 'var(--accent)', color: '#fff', fontFamily: 'var(--font)',
          }}
        >+ Add Auditor</button>
      </div>

      {/* Info Banner */}
      <div style={{
        background: 'var(--blue-bg)', border: '1px solid rgba(45,125,210,0.2)',
        borderRadius: 8, padding: '12px 16px', marginBottom: 20,
        fontSize: 12, color: 'var(--accent)', lineHeight: 1.6,
        display: 'flex', alignItems: 'flex-start', gap: 10,
      }}>
        <span style={{ marginTop: 1, flexShrink: 0 }}><IconShield /></span>
        <span>
          Auditors get read-only access to your assessment results, gap analysis,
          evidence locker, and reports. <strong>They cannot edit any data.</strong>{' '}
          Access links expire on the date you set and can be revoked at any time.
        </span>
      </div>

      {/* Auditor Table / Empty State */}
      {auditors.length === 0 ? (
        <div style={{
          background: 'var(--bg2)', border: '1px solid var(--border)',
          borderRadius: 10, padding: '56px 40px', textAlign: 'center',
        }}>
          <div style={{
            width: 52, height: 52, borderRadius: '50%', margin: '0 auto 16px',
            background: 'var(--surface2)', border: '1px solid var(--border2)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: 'var(--text3)',
          }}>
            <IconPerson />
          </div>
          <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)', marginBottom: 8 }}>
            No auditors added yet
          </div>
          <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 8, maxWidth: 400, margin: '0 auto 8px', lineHeight: 1.6 }}>
            Add your C3PAO assessor or compliance consultant to give them
            read-only access to your compliance posture.
          </div>
          <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 24, lineHeight: 1.5 }}>
            Auditors can view assessments, gap analysis, evidence, and tasks.
            They cannot make any changes.
          </div>
          <button
            onClick={() => { resetForm(); setShowModal(true); }}
            style={{
              fontSize: 12, fontWeight: 600, padding: '8px 20px', borderRadius: 6,
              cursor: 'pointer', border: '1px solid var(--accent)',
              background: 'var(--accent)', color: '#fff', fontFamily: 'var(--font)',
            }}
          >Add First Auditor</button>
        </div>
      ) : (
        <div style={{
          background: 'var(--bg2)', border: '1px solid var(--border)',
          borderRadius: 10, overflow: 'hidden',
        }}>
          {/* Table header */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 150px 120px 110px 120px 100px 120px',
            gap: 10, padding: '10px 16px', borderBottom: '1px solid var(--border)',
            fontSize: 10, color: 'var(--text3)', textTransform: 'uppercase',
            letterSpacing: '0.07em',
          }}>
            <div>Auditor</div>
            <div>Framework</div>
            <div>Access Link</div>
            <div>Created</div>
            <div>Expires</div>
            <div>Status</div>
            <div>Actions</div>
          </div>

          {auditors.map(a => {
            const daysLeft     = getDaysRemaining(a.expires_at);
            const isExpired    = daysLeft <= 0;
            const expiringSoon = daysLeft <= 7 && !isExpired && a.is_active;
            const fwLabel      = FW_LABEL[a.framework] || a.framework || 'CMMC L2';
            const isActive     = a.is_active && !isExpired;

            return (
              <div key={a.id} style={{
                display: 'grid',
                gridTemplateColumns: '1fr 150px 120px 110px 120px 100px 120px',
                gap: 10, padding: '14px 16px', borderBottom: '1px solid var(--border)',
                alignItems: 'center', fontSize: 12,
              }}>
                {/* Auditor info */}
                <div>
                  <div style={{ fontWeight: 500, color: 'var(--text)' }}>
                    {a.auditor_name || 'Unnamed Auditor'}
                  </div>
                  {a.auditor_firm && (
                    <div style={{ fontSize: 10, color: 'var(--text2)', marginTop: 1 }}>
                      {a.auditor_firm}
                    </div>
                  )}
                  {a.auditor_email && (
                    <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 1 }}>
                      {a.auditor_email}
                    </div>
                  )}
                </div>

                {/* Framework badge */}
                <div>
                  <span style={{
                    fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 4,
                    background: 'var(--blue-bg)', color: 'var(--accent)',
                    border: '1px solid rgba(45,125,210,0.2)', whiteSpace: 'nowrap',
                    display: 'inline-block',
                  }}>
                    {fwLabel}
                  </span>
                </div>

                {/* Copy link */}
                <div>
                  {isActive ? (
                    <button
                      onClick={() => copyLink(a.access_token, a.framework, a.id)}
                      style={{
                        fontSize: 11, fontWeight: 500, padding: '5px 10px', borderRadius: 5,
                        cursor: 'pointer', border: '1px solid var(--border2)',
                        background: copied === a.id ? 'var(--green-bg)' : 'var(--surface)',
                        color: copied === a.id ? 'var(--green)' : 'var(--text2)',
                        fontFamily: 'var(--font)', transition: 'all 0.15s',
                        display: 'flex', alignItems: 'center', gap: 5,
                      }}
                    >
                      {copied === a.id ? <IconCheck /> : <IconCopy />}
                      {copied === a.id ? 'Copied!' : 'Copy Link'}
                    </button>
                  ) : (
                    <span style={{ fontSize: 11, color: 'var(--text3)' }}>
                      {isExpired ? 'Expired' : 'Revoked'}
                    </span>
                  )}
                </div>

                {/* Created */}
                <div style={{ fontSize: 11, color: 'var(--text3)', fontFamily: 'var(--mono)' }}>
                  {a.created_at ? new Date(a.created_at).toLocaleDateString() : '—'}
                </div>

                {/* Expiry */}
                <div>
                  <div style={{
                    fontSize: 11,
                    color: isExpired ? 'var(--red)' : expiringSoon ? 'var(--amber)' : 'var(--text3)',
                    fontFamily: 'var(--mono)',
                  }}>
                    {formatExpiry(a.expires_at)}
                  </div>
                  {isExpired ? (
                    <div style={{ fontSize: 10, color: 'var(--red)', marginTop: 1 }}>Expired</div>
                  ) : (
                    <div style={{
                      fontSize: 10,
                      color: expiringSoon ? 'var(--amber)' : 'var(--text3)',
                      marginTop: 1,
                    }}>
                      {daysLeft} days left
                    </div>
                  )}
                </div>

                {/* Status */}
                <div>
                  <span style={{
                    fontSize: 10, fontWeight: 600, padding: '3px 8px', borderRadius: 10,
                    background: isActive ? 'var(--green-bg)' : 'var(--red-bg)',
                    color: isActive ? 'var(--green)' : 'var(--red)',
                    border: `1px solid ${isActive ? 'var(--green)' : 'var(--red)'}`,
                  }}>
                    {isActive ? 'Active' : isExpired ? 'Expired' : 'Revoked'}
                  </span>
                </div>

                {/* Actions */}
                <div style={{ display: 'flex', gap: 6 }}>
                  {isActive && (
                    <button
                      onClick={() => handleRevoke(a.id, a.auditor_name)}
                      style={{
                        fontSize: 10, padding: '4px 8px', borderRadius: 5,
                        cursor: 'pointer', border: '1px solid var(--amber)',
                        background: 'transparent', color: 'var(--amber)',
                        fontFamily: 'var(--font)',
                      }}
                    >Revoke</button>
                  )}
                  <button
                    onClick={() => handleDelete(a.id)}
                    style={{
                      fontSize: 10, padding: '4px 8px', borderRadius: 5,
                      cursor: 'pointer', border: '1px solid var(--red)',
                      background: 'transparent', color: 'var(--red)',
                      fontFamily: 'var(--font)',
                    }}
                  >Delete</button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* How It Works */}
      <div style={{
        background: 'var(--bg2)', border: '1px solid var(--border)',
        borderRadius: 10, padding: 20, marginTop: 16,
      }}>
        <div style={{
          fontSize: 13, fontWeight: 600, color: 'var(--text)',
          marginBottom: 14, paddingBottom: 10,
          borderBottom: '1px solid var(--border)',
        }}>
          How auditor access works
        </div>
        {[
          { step: '01', title: 'Add your auditor', desc: 'Enter their name, firm, select the framework being reviewed, and set an access duration.' },
          { step: '02', title: 'Share the access link', desc: 'Copy the link and send it to your C3PAO or consultant via email.' },
          { step: '03', title: 'Auditor views your data', desc: 'They open the link and see a read-only view of your full compliance posture for that framework.' },
          { step: '04', title: 'Revoke when done', desc: 'Remove access at any time. Links expire automatically on the date you set.' },
        ].map((item, i) => (
          <div key={item.step} style={{ display: 'flex', gap: 14, marginBottom: i < 3 ? 14 : 0 }}>
            <div style={{
              width: 28, height: 28, borderRadius: '50%', flexShrink: 0,
              background: 'var(--blue-bg)', border: '1px solid rgba(45,125,210,0.2)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 10, fontWeight: 700, color: 'var(--accent)', fontFamily: 'var(--mono)',
            }}>{item.step}</div>
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)', marginBottom: 2 }}>{item.title}</div>
              <div style={{ fontSize: 11, color: 'var(--text3)', lineHeight: 1.5 }}>{item.desc}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Invite Modal */}
      {showModal && (
        <div
          onClick={(e) => e.target === e.currentTarget && !loading && setShowModal(false)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)',
            zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 20,
          }}
        >
          <div style={{
            background: 'var(--bg2)', border: '1px solid var(--border2)',
            borderRadius: 12, padding: 28, width: 500, maxWidth: '100%',
            maxHeight: 'calc(100vh - 40px)', overflowY: 'auto',
          }}>
            {generatedRecord ? (
              /* ── Post-generation: show link ── */
              <div>
                <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text)', marginBottom: 4 }}>
                  Access Link Generated
                </div>
                <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 20 }}>
                  Share this link with{' '}
                  <strong style={{ color: 'var(--text2)' }}>
                    {generatedRecord.auditor_name}
                    {generatedRecord.auditor_firm ? ` · ${generatedRecord.auditor_firm}` : ''}
                  </strong>
                </div>

                {/* Framework + Expiry badges */}
                <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
                  <span style={{
                    fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 4,
                    background: 'var(--blue-bg)', color: 'var(--accent)',
                    border: '1px solid rgba(45,125,210,0.25)',
                  }}>
                    {FW_LABEL[generatedRecord.framework] || generatedRecord.framework}
                  </span>
                  <span style={{
                    fontSize: 11, fontWeight: 500, padding: '3px 10px', borderRadius: 4,
                    background: 'var(--surface2)', color: 'var(--text2)',
                  }}>
                    Expires {formatExpiry(generatedRecord.expires_at)}
                  </span>
                </div>

                {/* Copyable link box */}
                <div style={{
                  background: 'var(--surface)', border: '1px solid var(--border2)',
                  borderRadius: 8, padding: '10px 14px', marginBottom: 12,
                  display: 'flex', alignItems: 'center', gap: 10,
                }}>
                  <span style={{ color: 'var(--text3)', flexShrink: 0 }}><IconLink /></span>
                  <div style={{
                    fontSize: 11, color: 'var(--text2)', fontFamily: 'var(--mono)',
                    flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>
                    {getAuditorLink(generatedRecord.access_token, generatedRecord.framework)}
                  </div>
                  <button
                    onClick={() => copyLink(generatedRecord.access_token, generatedRecord.framework, 'modal')}
                    style={{
                      fontSize: 11, fontWeight: 600, padding: '5px 12px', borderRadius: 5, flexShrink: 0,
                      cursor: 'pointer', border: '1px solid var(--accent)',
                      background: copied === 'modal' ? 'var(--green)' : 'var(--accent)',
                      color: '#fff', fontFamily: 'var(--font)', transition: 'background 0.15s',
                      display: 'flex', alignItems: 'center', gap: 5,
                    }}
                  >
                    {copied === 'modal' ? <IconCheck /> : <IconCopy />}
                    {copied === 'modal' ? 'Copied!' : 'Copy'}
                  </button>
                </div>

                {/* Token display */}
                <div style={{
                  background: 'var(--surface)', border: '1px solid var(--border)',
                  borderRadius: 6, padding: '8px 14px', marginBottom: 20,
                  fontSize: 10, color: 'var(--text3)',
                }}>
                  Token: <span style={{ fontFamily: 'var(--mono)', color: 'var(--text2)', letterSpacing: '0.05em' }}>
                    {generatedRecord.access_token}
                  </span>
                </div>

                <div style={{
                  background: 'var(--green-bg)', border: '1px solid var(--green)',
                  borderRadius: 6, padding: '10px 14px', marginBottom: 20,
                  fontSize: 12, color: 'var(--green)', lineHeight: 1.5,
                  display: 'flex', alignItems: 'center', gap: 8,
                }}>
                  <IconCheck />
                  Access link created. Share it with your auditor. It will expire on{' '}
                  <strong>{formatExpiry(generatedRecord.expires_at)}</strong>.
                </div>

                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <button
                    onClick={() => setShowModal(false)}
                    style={{
                      fontSize: 12, fontWeight: 600, padding: '8px 20px', borderRadius: 6,
                      cursor: 'pointer', border: '1px solid var(--accent)',
                      background: 'var(--accent)', color: '#fff', fontFamily: 'var(--font)',
                    }}
                  >Done</button>
                </div>
              </div>
            ) : (
              /* ── Invite form ── */
              <div>
                <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text)', marginBottom: 4 }}>
                  Add Auditor
                </div>
                <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 20 }}>
                  Generate a read-only access link for your C3PAO assessor or compliance consultant.
                </div>

                {/* Name + Firm row */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
                  <div>
                    <label style={labelStyle}>Auditor Name *</label>
                    <input
                      type="text"
                      placeholder="e.g. John Smith"
                      value={form.name}
                      onChange={e => setForm(prev => ({ ...prev, name: e.target.value }))}
                      style={inputStyle}
                      onFocus={e => e.target.style.borderColor = 'var(--accent)'}
                      onBlur={e => e.target.style.borderColor = 'var(--border2)'}
                    />
                  </div>
                  <div>
                    <label style={labelStyle}>Auditor Firm *</label>
                    <input
                      type="text"
                      placeholder="e.g. ABCD C3PAO"
                      value={form.firm}
                      onChange={e => setForm(prev => ({ ...prev, firm: e.target.value }))}
                      style={inputStyle}
                      onFocus={e => e.target.style.borderColor = 'var(--accent)'}
                      onBlur={e => e.target.style.borderColor = 'var(--border2)'}
                    />
                  </div>
                </div>

                {/* Email */}
                <div style={{ marginBottom: 14 }}>
                  <label style={labelStyle}>
                    Email Address
                    <span style={{ marginLeft: 6, color: 'var(--text3)', textTransform: 'none', fontSize: 10 }}>
                      (optional — for reference only)
                    </span>
                  </label>
                  <input
                    type="email"
                    placeholder="e.g. auditor@c3pao.com"
                    value={form.email}
                    onChange={e => setForm(prev => ({ ...prev, email: e.target.value }))}
                    style={inputStyle}
                    onFocus={e => e.target.style.borderColor = 'var(--accent)'}
                    onBlur={e => e.target.style.borderColor = 'var(--border2)'}
                  />
                </div>

                {/* Framework + Expiry */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
                  <div>
                    <label style={labelStyle}>Framework Being Audited</label>
                    <select
                      value={form.framework}
                      onChange={e => setForm(prev => ({ ...prev, framework: e.target.value }))}
                      style={{ ...inputStyle, cursor: 'pointer' }}
                    >
                      {ALL_FRAMEWORKS.map(f => (
                        <option key={f.id} value={f.id}>{f.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label style={labelStyle}>Access Duration</label>
                    <select
                      value={form.expiryDays}
                      onChange={e => setForm(prev => ({ ...prev, expiryDays: Number(e.target.value) }))}
                      style={{ ...inputStyle, cursor: 'pointer' }}
                    >
                      <option value={30}>30 days</option>
                      <option value={60}>60 days</option>
                      <option value={90}>90 days</option>
                      <option value={180}>180 days</option>
                      <option value={365}>1 year</option>
                    </select>
                  </div>
                </div>

                {error && (
                  <div style={{
                    background: 'var(--red-bg)', border: '1px solid var(--red)',
                    borderRadius: 6, padding: '10px 12px', fontSize: 12,
                    color: 'var(--red)', marginBottom: 14,
                  }}>{error}</div>
                )}

                <div style={{
                  background: 'var(--surface)', borderRadius: 8,
                  padding: '10px 14px', marginBottom: 20,
                  fontSize: 11, color: 'var(--text3)', lineHeight: 1.6,
                }}>
                  A unique access link will be generated for the{' '}
                  <strong style={{ color: 'var(--text2)' }}>
                    {FW_LABEL[form.framework] || form.framework}
                  </strong>{' '}
                  framework, valid for{' '}
                  <strong style={{ color: 'var(--text2)' }}>
                    {form.expiryDays === 365 ? '1 year' : `${form.expiryDays} days`}
                  </strong>.
                </div>

                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                  <button
                    onClick={() => { setShowModal(false); resetForm(); }}
                    disabled={loading}
                    style={{
                      fontSize: 12, fontWeight: 500, padding: '8px 16px', borderRadius: 6,
                      cursor: loading ? 'not-allowed' : 'pointer', border: '1px solid var(--border2)',
                      background: 'transparent', color: 'var(--text2)', fontFamily: 'var(--font)',
                    }}
                  >Cancel</button>
                  <button
                    onClick={handleInvite}
                    disabled={loading || !form.name.trim() || !form.firm.trim()}
                    style={{
                      fontSize: 12, fontWeight: 600, padding: '8px 20px', borderRadius: 6,
                      cursor: loading || !form.name.trim() || !form.firm.trim() ? 'not-allowed' : 'pointer',
                      border: '1px solid var(--accent)',
                      background: 'var(--accent)', color: '#fff',
                      fontFamily: 'var(--font)',
                      opacity: loading || !form.name.trim() || !form.firm.trim() ? 0.65 : 1,
                    }}
                  >
                    {loading ? 'Generating...' : 'Generate Access Link'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
